/**
 * Wave D — block system commands: define, insert, attributes, visibility
 * states, authoring palettes, plus the attribute/express-tool helpers
 * (TXT2MTXT, BURST, TCIRCLE, TCOUNT) that operate on text/block entities.
 * (docs/architecture/command-parity-plan.md)
 */

import {
    runInteraction, PT, PN, PK, PTxt, PLoopK, PLoop, requireSelection, entityToPolyline,
} from './lib';
import { undoableAdd, undoableModify, undoableDelete, makeEntity } from '../entityOps';
import { useAppStore, type Entity, type Layer } from '../../../state/store';
import { useBlockStore, useCountStore, type BlockAttribute, type BlockDef } from '../../../state/commandAuxStores';
import type { Pt3 } from '../geometryMath';
import type { RunnerMap } from '../runnerRegistry';
import { notify } from '../../../utils/notify';
import { openDxfExportDialog } from '../../../services/cad/dxf/exportDialogBridge';
import { aciToHex } from '../../../components/cad/aciColor';
import { pushRecentBlock } from '../../../components/cad/blockPalettePrefs';
import i18n from '../../../i18n';

/** Deep-translate a data bag by a delta (used by INSERT placement). */
function translateData(data: Record<string, any>, d: [number, number, number]): Record<string, any> {
    const shift = (p: number[]): number[] => [p[0] + d[0], p[1] + d[1], (p[2] ?? 0) + d[2]];
    ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k])) data[k] = shift(data[k] as number[]); });
    ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as number[][]).map(shift); });
    return data;
}

/** Point-ish bag fields every placement transform walks (same set the
 *  WBLOCK base translation covers, so one list stays the contract). */
const POINT_FIELDS = ['start', 'end', 'position', 'center', 'textPosition', 'point1', 'point2', 'radiusPoint', 'tip', 'landing', 'corner1', 'corner2'];
const POINT_LIST_FIELDS = ['vertices', 'controlPoints', 'boundary', 'arcPts'];

/** Scale+rotate a data bag's points around the local origin (the block
 *  definition coordinate space) — the geometry half of EXPLode placement. */
function scaleRotateData(data: Record<string, any>, scale: number, rotRad: number): Record<string, any> {
    const cos = Math.cos(rotRad), sin = Math.sin(rotRad);
    const xf = (p: number[]): number[] => {
        const x = (p[0] ?? 0) * scale, y = (p[1] ?? 0) * scale;
        return [x * cos - y * sin, x * sin + y * cos, p[2] ?? 0];
    };
    POINT_FIELDS.forEach((k) => { if (Array.isArray(data[k])) data[k] = xf(data[k] as number[]); });
    POINT_LIST_FIELDS.forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as number[][]).map(xf); });
    return data;
}

/** INSERT placement options (Faz 5.8, OCS BlockInsertOptions parity). The
 *  insert reference itself carries one uniform scale (the render motor's
 *  model); the X/Y split waits for per-axis reference scaling. */
export interface BlockInsertOptions {
    scale: number;
    rotationDeg: number;
    /** Place the definition's components directly instead of a reference. */
    explode: boolean;
}

/**
 * Place a block definition at a world point (panel click / drop + the INSERT
 * runner share this). Returns the command-line summary line.
 */
export function placeBlock(name: string, at: Pt3, opts: BlockInsertOptions = { scale: 1, rotationDeg: 0, explode: false }): string {
    const def = useBlockStore.getState().defs[name];
    if (!def) return `INSERT: no block "${name}".`;
    // Every successful placement feeds the palette's Recent tab (Faz 5.8).
    pushRecentBlock(name);
    if (opts.explode) {
        const rot = (opts.rotationDeg * Math.PI) / 180;
        const created = def.entities.map((src) => makeEntity(
            src.type === 'attdef' ? 'attdef' : src.type,
            `${name}-ex-${src.name}`,
            translateData(scaleRotateData(structuredClone(src.data), opts.scale, rot), [at[0], at[1], at[2] ?? 0]),
            src.layer,
        ));
        undoableAdd(created);
        return `INSERT: "${name}" exploded at ${at[0].toFixed(2)},${at[1].toFixed(2)} (${created.length} component(s)).`;
    }
    // A single reference entity; EntityLayer renders the definition's
    // components at the insertion point (and BURST/EXPLODEBLOCK expand it).
    // Attribute defaults seed the instance values so they render + edit.
    const attributeValues: Record<string, string> = {};
    def.attributes.forEach((a) => { attributeValues[a.tag] = a.defaultValue; });
    undoableAdd([makeEntity('insert', `${name}-ref`, {
        blockName: name, position: [at[0], at[1], at[2] ?? 0],
        scale: opts.scale, rotation: (opts.rotationDeg * Math.PI) / 180,
        attributeValues,
    })]);
    return `INSERT: "${name}" placed at ${at.map((v) => v.toFixed(2)).join(',')} (scale ${opts.scale}, rot ${opts.rotationDeg}°, ${def.entities.length} component(s)).`;
}

function centroidOf(list: Entity[]): Pt3 {
    const pts: Pt3[] = [];
    list.forEach((e) => pts.push(...(entityToPolyline(e) ?? [])));
    if (pts.length === 0) return [0, 0, 0];
    const c = pts.reduce((acc, p) => [acc[0] + p[0] / pts.length, acc[1] + p[1] / pts.length, acc[2] / pts.length], [0, 0, 0]);
    return c as Pt3;
}

/** Deep-translate a wblock selection by -base (every point-ish bag field). */
function translateForWblock(data: Record<string, any>, base: number[]): Record<string, any> {
    const shift = (p: number[]): number[] => [p[0] - base[0], p[1] - base[1], (p[2] ?? 0) - base[2]];
    ['start', 'end', 'position', 'center', 'textPosition', 'point1', 'point2', 'radiusPoint', 'tip', 'landing', 'corner1', 'corner2']
        .forEach((k) => { if (Array.isArray(data[k])) data[k] = shift(data[k] as number[]); });
    ['vertices', 'controlPoints', 'boundary', 'arcPts']
        .forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as number[][]).map(shift); });
    return data;
}

/**
 * WBLOCK dependency closure (§3.2 decision 14): the referenced block
 * definitions (nested INSERTs resolved transitively, attribute
 * definitions included by definition) plus every layer the selection and
 * the closed block content draw on — the saved file is self-contained.
 */
function wblockDependencyClosure(selection: Entity[]): { blocks: BlockDef[]; layers: Layer[] } {
    const defs = useBlockStore.getState().defs;
    const app = useAppStore.getState();
    const usedBlocks = new Set<string>();
    const queue = selection
        .filter((e) => e.type === 'insert' || e.type === 'block')
        .map((e) => String((e.data as Record<string, unknown>).blockName ?? ''));
    while (queue.length > 0) {
        const name = queue.pop()!;
        if (!name || usedBlocks.has(name) || !defs[name]) continue;
        usedBlocks.add(name);
        defs[name].entities.forEach((c) => {
            if (c.type === 'insert' || c.type === 'block') {
                queue.push(String((c.data as Record<string, unknown>).blockName ?? ''));
            }
        });
    }
    const blocks = [...usedBlocks].map((n) => defs[n]).filter(Boolean) as BlockDef[];
    const usedLayerIds = new Set<string>();
    selection.forEach((e) => usedLayerIds.add(e.layer));
    blocks.forEach((b) => b.entities.forEach((e) => usedLayerIds.add(e.layer)));
    const layers = [...usedLayerIds].map((id) => app.layers[id]).filter(Boolean) as Layer[];
    return { blocks, layers };
}

export const blockRunners: RunnerMap = {
    'blk.define': (args) => {
        const name = args?.join(' ')?.trim();
        if (name) return defineBlock(name);
        runInteraction({
            steps: [PTxt('BLOCK: name'), PK('BLOCK: pick objects to bundle', [])],
            complete: (_p, values, picks) => {
                const label = String(values[0] ?? `Block-${Date.now()}`).trim();
                return defineBlockWith(label, picks);
            },
        });
        return 'BLOCK: enter name';
    },

    'blk.insert': (args) => {
        const name = args?.[0];
        const defs = useBlockStore.getState().defs;
        if (!name || !defs[name]) {
            const list = Object.keys(defs);
            return list.length
                ? `INSERT: known blocks: ${list.join(', ')} — usage: INSERT <name>`
                : 'INSERT: no block definitions yet (use BLOCK first).';
        }
        // Panel-supplied option flags ride the args tail: INSERT <name>
        // [scale] [rotation] [explode] [repeat] (Faz 5.8 options strip).
        const flags = (args ?? []).slice(1).map((a) => String(a).toLowerCase());
        const numbers = flags.filter((f) => f !== 'explode' && f !== 'repeat').map(Number).filter((n) => Number.isFinite(n));
        const opts: BlockInsertOptions = {
            scale: numbers[0] !== undefined && numbers[0] > 0 ? numbers[0] : 1,
            rotationDeg: numbers[1] !== undefined ? numbers[1] : 0,
            explode: flags.includes('explode'),
        };
        const repeat = flags.includes('repeat');
        if (repeat) {
            // Multiple-insert semantics: one block per picked point until the
            // interaction ends (Escape / empty Enter).
            runInteraction({
                steps: [PLoop(`INSERT "${name}": insertion point (Repeat)`, 1)],
                complete: (pts) => pts.map((p) => placeBlock(name, [p.x, p.y, p.z], opts)).slice(-1)[0]
                    ?? `INSERT: ${pts.length} placement(s).`,
            });
            return `INSERT "${name}": pick placement points (Enter to finish)`;
        }
        runInteraction({
            steps: [
                PT('INSERT: insertion point'),
                PN(`INSERT: scale factor <${opts.scale}>`, opts.scale),
                PN(`INSERT: rotation angle (deg) <${opts.rotationDeg}>`, opts.rotationDeg),
            ],
            complete: (pts, values) => {
                const scale = Number(values[1]) || opts.scale;
                const rot = Number(values[2]) || opts.rotationDeg;
                return placeBlock(name, [pts[0].x, pts[0].y, pts[0].z], { ...opts, scale, rotationDeg: rot });
            },
        });
        return `INSERT "${name}": specify point, scale, rotation`;
    },

    'blk.wblock': (args) => {
        const name = args?.join(' ')?.trim();
        const { defs } = useBlockStore.getState();
        let selection: Entity[] = [];
        let suggested = 'wblock';
        if (name && defs[name]) {
            // Block-name flow: the definition travels as one INSERT reference.
            const def = defs[name];
            const attributeValues: Record<string, string> = {};
            def.attributes.forEach((a) => { attributeValues[a.tag] = a.defaultValue; });
            selection = [makeEntity('insert', `${name}-wblock`, {
                blockName: name, position: [0, 0, 0], scale: 1, rotation: 0, attributeValues,
            })];
            suggested = name;
        } else {
            const picked = requireSelection('WBLOCK');
            if (!picked) return 'WBLOCK: select objects or specify an existing block name.';
            selection = picked;
            suggested = 'selection';
        }
        runInteraction({
            // OCS wblock.rs parity: the base point is asked first and the
            // whole selection is translated by -base (base becomes origin).
            steps: [PT('WBLOCK: base point (selection is translated by -base)')],
            complete: (pts) => {
                const base = [pts[0].x, pts[0].y, pts[0].z];
                const moved = selection.map((e) => ({ ...e, data: translateForWblock(structuredClone(e.data), base) }));
                const closure = wblockDependencyClosure(moved);
                // The real .dxf write happens in the version-target dialog
                // (dependency-closed subset → writeDxfNative → save dialog).
                openDxfExportDialog({
                    mode: 'wblock', fileName: suggested,
                    entities: moved, blocks: closure.blocks, layers: closure.layers,
                });
                return `WBLOCK: DXF save dialog opened (${moved.length} object(s), ${closure.blocks.length} block definition(s)).`;
            },
        });
        return 'WBLOCK: specify base point';
    },

    // BEDIT — the isolated block editing session (Faz 5.15): the working set
    // is placed tagged `data.blockScope = <name>` (EntityLayer renders only
    // that scope) and the Block Editor contextual tab (Faz 3.12) mounts.
    'blk.edit': (args) => {
        const name = args?.[0];
        const block = useBlockStore.getState();
        if (!name || !block.defs[name]) return 'BEDIT: specify a block name.';
        const def = block.defs[name];
        if (block.editingBlock) {
            return `BEDIT: "${block.editingBlock.name}" is already open — BCLOSE first.`;
        }
        block.setEditingBlock({ name, basePoint: def.basePoint });
        // Populate the working set as scope copies of the definition (the
        // store's addEntity re-tags defensively, the explicit tag keeps the
        // undo snapshot honest).
        const ids = undoableAdd(def.entities.map((e) => ({
            ...e,
            id: `cad:${crypto.randomUUID()}`,
            data: { ...structuredClone(e.data), blockScope: name },
        })));
        useAppStore.getState().select(ids);
        notify(i18n.t('tools.notify.beditOpened', { name: name }), 'info');
        return `BEDIT: editing "${name}" in isolation (${ids.length} component(s)) — BSAVE/BSAVEAS/BCLOSE to leave.`;
    },

    // BSAVE — convert the scope working set back into the block definition
    // (ATTSYNC semantics: BlockRefMesh reads defs live, so every dependent
    // INSERT refreshes on the spot; attribute instances keep their values).
    'blk.bsave': () => {
        const session = useBlockStore.getState().editingBlock;
        if (!session) return 'BSAVE: no Block Editor session open.';
        const count = saveScopeToDef(session.name, session.basePoint);
        if (count < 0) return 'BSAVE: the working set is empty — nothing saved.';
        return `BSAVE: "${session.name}" updated from ${count} component(s); dependent INSERT(s) refreshed.`;
    },

    // BSAVEAS — save the working set under a NEW name and keep editing it.
    'blk.bsaveAs': (args) => {
        const session = useBlockStore.getState().editingBlock;
        if (!session) return 'BSAVEAS: no Block Editor session open.';
        const run = (newName: string) => {
            const block = useBlockStore.getState();
            if (block.defs[newName] && newName !== session.name) {
                return `BSAVEAS: "${newName}" already exists.`;
            }
            // Retag the working set to the new scope, then save under it.
            const scoped = scopeEntityIds(session.name);
            undoableModify(scoped, (d) => ({ ...d, blockScope: newName }));
            block.setEditingBlock({ name: newName, basePoint: session.basePoint });
            const count = saveScopeToDef(newName, session.basePoint);
            if (count < 0) return 'BSAVEAS: the working set is empty — nothing saved.';
            return `BSAVEAS: saved as "${newName}" (${count} component(s)); still editing.`;
        };
        const name = args?.join(' ')?.trim();
        if (name) return run(name);
        runInteraction({
            steps: [PTxt('BSAVEAS: new block name')],
            complete: (_p, values) => run(String(values[0] ?? '').trim()),
        });
        return 'BSAVEAS: enter the new block name';
    },

    // BCLOSE — leave the session (Save by default, Discard by keyword); the
    // working set is removed either way, the definition keeps the content.
    'blk.bclose': (args) => {
        const session = useBlockStore.getState().editingBlock;
        if (!session) return 'BCLOSE: no Block Editor session open.';
        const mode = (args?.[0] ?? '').toLowerCase();
        if (mode === 'save' || mode === 'discard') {
            return closeSession(session, mode === 'save');
        }
        runInteraction({
            steps: [{
                prompt: 'BCLOSE: save changes before closing? (Save/Discard) <Save>',
                kind: 'text' as const,
                options: [{ keyword: 'Save' }, { keyword: 'Discard' }],
            }],
            complete: (_p, values) => {
                const pick = String(values[0] ?? 'Save').toLowerCase();
                return closeSession(session, pick !== 'discard');
            },
        });
        return 'BCLOSE: Save or Discard?';
    },

    // BTESTBLOCK — drop a throwaway INSERT of the block being edited.
    'blk.btestBlock': () => {
        const session = useBlockStore.getState().editingBlock;
        if (!session) return 'BTESTBLOCK: open a block with BEDIT first.';
        runInteraction({
            steps: [PT(`BTESTBLOCK "${session.name}": test insertion point`)],
            complete: (pts) => placeBlock(session.name, [pts[0].x, pts[0].y, pts[0].z]),
        });
        return 'BTESTBLOCK: specify the test insertion point';
    },

    'blk.attdef': () => {
        // Faz 8.9: the dialog carries tag/prompt/default + the AFLAGS mode
        // checkboxes + text settings; placement runs through the interaction
        // engine (the dialog's Insert button starts it).
        useAppStore.getState().openManageDialog('attdef');
        return 'ATTDEF: Attribute Definition dialog opened.';
    },

    'blk.attMan': () => {
        // ATTMAN (Faz 8.10): the attribute editor dialog for the selected
        // INSERT (Attribute / TextOptions / Properties tabs).
        const { entities, selectedIds } = useAppStore.getState();
        const hasInsert = selectedIds.some((id) => entities[id]?.type === 'insert');
        if (!hasInsert) return 'ATTMAN: select a block reference (INSERT) first.';
        useAppStore.getState().openManageDialog('attman');
        return 'ATTMAN: Attribute Manager dialog opened.';
    },

    // ATTSYNC (Faz 8.10, OCS blocks.rs algorithm verbatim): definition
    // attributes (tag, default) drive every matching INSERT — surviving
    // attribute values are retained, removed tags drop, missing tags append
    // at the END with their default (order NOT synced). ONE undo step.
    'blk.attsync': (args) => {
        const block = useBlockStore.getState();
        const defNames = Object.keys(block.defs);
        if (defNames.length === 0) return 'ATTSYNC: no block definitions.';
        const run = (name: string): string => {
            const def = block.defs[name];
            if (!def) return `ATTSYNC: no block "${name}".`;
            const wanted = def.attributes
                .map((a) => ({ tag: String(a.tag ?? ''), def: String(a.defaultValue ?? '') }))
                .filter((a) => a.tag);
            const app = useAppStore.getState();
            const inserts = Object.values(app.entities).filter((e) => {
                if (e.type !== 'insert') return false;
                if (String((e.data as Record<string, unknown>).blockName ?? '') !== name) return false;
                const layer = app.layers[e.layer];
                return !layer?.locked; // locked-layer INSERTs are rejected
            });
            if (inserts.length === 0) return `ATTSYNC "${name}": no matching block reference(s).`;
            const ids = inserts.map((e) => e.id);
            undoableModify(ids, (d) => {
                const values = { ...((d.attributeValues as Record<string, string>) ?? {}) };
                const next: Record<string, string> = {};
                wanted.forEach((a) => {
                    // Survivors keep their value; missing tags join at the end.
                    next[a.tag] = a.tag in values ? values[a.tag] : a.def;
                });
                return { ...d, attributeValues: next };
            });
            return `ATTSYNC: ${ids.length} block reference(s) synced against ${wanted.length} attribute definition(s) of "${name}".`;
        };
        const name = args?.join(' ')?.trim();
        if (name) return run(name);
        runInteraction({
            steps: [{
                prompt: `ATTSYNC: block name (${defNames.slice(0, 6).join('/')}${defNames.length > 6 ? '/…' : ''})`,
                kind: 'text' as const,
            }],
            complete: (_p, values) => run(String(values[0] ?? '').trim()),
        });
        return 'ATTSYNC: specify the block name';
    },

    'blk.attEdit': () => {
        runInteraction({
            steps: [PK('EATTEDIT: pick a block reference'), PTxt('EATTEDIT: Tag=Value pairs (comma)')],
            complete: (_p, values, picks) => {
                const entries: Record<string, string> = {};
                String(values[0] ?? '').split(',').forEach((s) => {
                    const [k = '', v = ''] = s.split('=');
                    if (k.trim()) entries[k.trim()] = v;
                });
                if (Object.keys(entries).length === 0) return 'EATTEDIT: no Tag=Value pairs given.';
                undoableModify(picks, (d) => ({ ...d, attributeValues: { ...(d.attributeValues as object ?? {}), ...entries } }));
                return `EATTEDIT: ${Object.keys(entries).length} attribute value(s) updated.`;
            },
        });
        return 'EATTEDIT: pick a block reference';
    },

    'blk.param': (args) => {
        const run = (name: string) => {
            const kind = (args?.[0] ?? 'Linear').replace(/^./, (c) => c.toUpperCase());
            useBlockStore.getState().addParameter(name, kind);
            return `BPARAMETER: ${kind} parameter added to "${name}".`;
        };
        const { defs } = useBlockStore.getState();
        const list = Object.keys(defs);
        if (list.length === 0) return 'BPARAMETER: define a block first.';
        runInteraction({ steps: [PTxt(`BPARAMETER: block (${list.join('/')})`)], complete: (_p, v) => run(String(v[0] ?? list[0])) });
        return 'BPARAMETER: choose block';
    },

    'blk.action': (args) => {
        const { defs } = useBlockStore.getState();
        const list = Object.keys(defs);
        if (list.length === 0) return 'BACTION: define a block first.';
        runInteraction({
            steps: [PTxt(`BACTION: block (${list.join('/')})`), PTxt('BACTION: action kind (Stretch/Move/Scale)')],
            complete: (_p, v) => {
                const name = String(v[0] ?? list[0]);
                const def = defs[name];
                const param = def?.parameters?.[0]?.id;
                if (!param) return `BACTION: "${name}" has no parameter to bind.`;
                useBlockStore.getState().addAction(name, String(v[1] ?? 'Stretch'), param);
                return `BACTION: action bound to ${param} on "${name}".`;
            },
        });
        return 'BACTION: choose block';
    },

    // BVSTATE: two behaviors — (1) with a selected block insert, set its
    // active visibility state (real render effect via BlockRefMesh);
    // (2) with no selection, register a new state name on a definition.
    'blk.visState': (args) => {
        const { defs, addVisState } = useBlockStore.getState();
        const list = Object.keys(defs);
        if (list.length === 0) return 'BVSTATE: define a block first.';
        const { entities, selectedIds } = useAppStore.getState();
        const insert = selectedIds.find((id) => entities[id]?.type === 'insert');
        if (insert) {
            const e = entities[insert];
            const name = String(e.data.blockName ?? '');
            const states = defs[name]?.visibilityStates ?? [];
            const wanted = args?.join(' ').trim();
            const state = wanted && states.includes(wanted) ? wanted : (states[0] ?? null);
            if (!state) return `BVSTATE: "${name}" has no visibility states (create with BVSTATE <name>).`;
            undoableModify([insert], (d) => ({ ...d, visibilityState: d.visibilityState === state ? '' : state }));
            return `BVSTATE: instance toggled toward "${state}".`;
        }
        runInteraction({
            steps: [PTxt(`BVSTATE: block (${list.join('/')})`), PTxt('BVSTATE: new state name')],
            complete: (_p, v) => {
                const name = String(v[0] ?? list[0]);
                const state = String(v[1] ?? `State-${(defs[name]?.visibilityStates?.length ?? 0) + 1}`).trim();
                addVisState(name, state);
                return `BVSTATE: "${state}" added to "${name}" (tag components with BLOCKVISIBILITYPARAMETERS, then set via BVSTATE on an insert).`;
            },
        });
        return 'BVSTATE: choose block';
    },

    'blk.authorPalette': () => {
        notify(i18n.t('tools.notify.bauthorpaletteDynamicBlockToolsAvailableVia'), 'info');
        return 'Block Authoring Palette: use BPARAMETER, BACTION, BVSTATE.';
    },

    // ------------- text / attribute helpers -------------
    'blk.mleaderText2mtext': () => {
        const list = requireSelection('TXT2MTXT');
        if (!list) return 'TXT2MTXT: select single-line texts first.';
        const texts = list.filter((e) => e.type === 'text' && !e.data.multiline);
        if (texts.length < 2) return 'TXT2MTXT: pick 2+ single-line texts.';
        const joined = texts.map((e) => String((e.data as Record<string, unknown>).content ?? '')).join('\n');
        const first = texts[0];
        const pos = (first.data.position as number[]) ?? [0, 0, 0];
        undoableDelete(texts.map((e) => e.id));
        undoableAdd([makeEntity('mtext', `Merged-${Date.now()}`, {
            content: joined, position: pos, styleId: 'standard', rotation: 0, multiline: true,
        })]);
        return `TXT2MTXT: ${texts.length} texts merged into one.`;
    },

    'blk.burst': () => {
        runInteraction({
            steps: [PK('BURST: pick a block reference')],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e || e.type !== 'insert') return 'BURST: pick a block reference.';
                const def = useBlockStore.getState().defs[String((e.data as Record<string, unknown>).blockName ?? '')];
                if (!def) return 'BURST: block definition missing.';
                const p = (e.data.position as number[]) ?? [0, 0, 0];
                const vals = (e.data.attributeValues as Record<string, string>) ?? {};
                const created = def.entities.map((src) => {
                    const clone = { ...src, id: `cad:${crypto.randomUUID()}`, data: translateData(structuredClone(src.data), [p[0] ?? 0, p[1] ?? 0, p[2] ?? 0]) };
                    if (src.type === 'attdef') {
                        const tag = String(src.data.tag ?? '');
                        return makeEntity('text', `Burst-${tag}`, {
                            content: vals[tag] ?? String(src.data.defaultValue ?? tag),
                            position: clone.data.position ?? [0, 0, 0], styleId: 'standard', rotation: 0, multiline: false,
                        });
                    }
                    return clone;
                });
                undoableDelete([picks[0]]);
                undoableAdd(created);
                return `BURST: ${created.length} component(s) exploded, attributes kept as plain text.`;
            },
        });
        return 'BURST: pick a block';
    },

    'blk.tcircle': () => {
        runInteraction({
            steps: [PLoopK('TCIRCLE: pick texts', 1), PTxt('TCIRCLE: shape (Circles/Slots/Rectangles)')],
            complete: (_p, values, picks) => {
                const { entities } = useAppStore.getState();
                const shape = String(values[0] ?? 'Circles');
                let n = 0;
                picks.forEach((id) => {
                    const e = entities[id];
                    if (!e || !['text', 'mtext'].includes(e.type)) return;
                    const p = (e.data.position as number[]) ?? [0, 0, 0];
                    const content = String(e.data.content ?? '');
                    const r = Math.max(0.4, content.length * 0.12);
                    if (shape.startsWith('Rect')) {
                        undoableAdd([makeEntity('rectangle', `TCircle-${Date.now()}`, { corner1: [p[0] - r, p[1] - r * 0.7, p[2] ?? 0], corner2: [p[0] + r, p[1] + r * 0.7, p[2] ?? 0], color: '#ffffff' })]);
                    } else {
                        undoableAdd([makeEntity('circle', `TCircle-${Date.now()}`, { center: [p[0], p[1], p[2] ?? 0], radius: r, color: '#ffffff' })]);
                    }
                    n++;
                });
                return `TCIRCLE: ${n} frame(s) added (${shape}).`;
            },
        });
        return 'TCIRCLE: pick texts';
    },

    'blk.tcount': (args) => {
        const start = Number(args?.[0]) || 1;
        const step = Number(args?.[1]) || 1;
        runInteraction({
            steps: [PLoopK('TCOUNT: pick texts in order', 1)],
            complete: (_p, _v, picks) => {
                let i = start;
                picks.forEach((id) => {
                    undoableModify([id], (d) => { const out = { ...d, content: String(i) }; i += step; return out; });
                });
                return `TCOUNT: ${picks.length} text(s) numbered from ${start}.`;
            },
        });
        return `TCOUNT: numbering from ${start}`;
    },

    'blk.clipit': () => {
        runInteraction({
            steps: [PK('CLIPIT: pick the clip boundary'), PK('CLIPIT: pick the object to clip')],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const boundary = entityToPolyline(entities[picks[0]]);
                if (!boundary || boundary.length < 3) return 'CLIPIT: the boundary is not a closed polygon.';
                undoableModify([picks[1]], (d) => ({ ...d, clipBoundary: boundary }));
                return 'CLIPIT: clip boundary applied (reference contents outside it hide).';
            },
        });
        return 'CLIPIT: pick boundary then object';
    },

    // ------------- COUNT (Faz 5.9: live block counting palette) -------------
    'blk.count': (args) => {
        const defs = useBlockStore.getState().defs;
        const list = Object.keys(defs);
        if (list.length === 0) return 'COUNT: no block definitions yet (use BLOCK first).';
        const areaMode = (args?.[0] ?? '').toLowerCase();
        const pickArea = (target: string): string => {
            if (areaMode === 'model' || areaMode === 'all') return startCount(target, null);
            if (areaMode === 'rect' || areaMode === 'rectangle') {
                runInteraction({
                    steps: [
                        PT('COUNT: first corner of the count area'),
                        PT('COUNT: opposite corner'),
                    ],
                    complete: (pts) => startCount(target, [
                        [pts[0].x, pts[0].y], [pts[1].x, pts[0].y],
                        [pts[1].x, pts[1].y], [pts[0].x, pts[1].y],
                    ]),
                });
                return 'COUNT: specify the rectangular area';
            }
            if (areaMode === 'poly' || areaMode === 'polygon') {
                runInteraction({
                    steps: [PLoop('COUNT: count area boundary points (Enter to finish)', 3)],
                    complete: (pts) => startCount(target, pts.map((p) => [p.x, p.y] as [number, number])),
                });
                return 'COUNT: pick the polygonal area boundary';
            }
            runInteraction({
                steps: [{
                    prompt: 'COUNT: area (Model/Rectangle/Polygon) <Model>',
                    kind: 'text' as const,
                    options: [{ keyword: 'Model' }, { keyword: 'Rectangle' }, { keyword: 'Polygon' }],
                }],
                complete: (_p, values) => pickArea2(String(values[0] ?? 'Model').toLowerCase(), target),
            });
            return 'COUNT: choose the count area';
        };
        const pickArea2 = (mode: string, target: string): string => {
            if (mode === 'rect' || mode === 'rectangle') return pickAreaRect(target);
            if (mode === 'poly' || mode === 'polygon') return pickAreaPoly(target);
            return startCount(target, null);
        };
        const pickAreaRect = (target: string): string => {
            runInteraction({
                steps: [PT('COUNT: first corner of the count area'), PT('COUNT: opposite corner')],
                complete: (pts) => startCount(target, [
                    [pts[0].x, pts[0].y], [pts[1].x, pts[0].y],
                    [pts[1].x, pts[1].y], [pts[0].x, pts[1].y],
                ]),
            });
            return 'COUNT: specify the rectangular area';
        };
        const pickAreaPoly = (target: string): string => {
            runInteraction({
                steps: [PLoop('COUNT: count area boundary points (Enter to finish)', 3)],
                complete: (pts) => startCount(target, pts.map((p) => [p.x, p.y] as [number, number])),
            });
            return 'COUNT: pick the polygonal area boundary';
        };
        const name = args?.join(' ')?.trim();
        // Bare "COUNT <block>" jumps straight to the model-wide tally.
        if (name && defs[name]) return startCount(name, null);
        runInteraction({
            steps: [{
                prompt: `COUNT: block name (${list.slice(0, 6).join('/')}${list.length > 6 ? '/…' : ''}, * = all)`,
                kind: 'text' as const,
            }],
            complete: (_p, values) => {
                const target = String(values[0] ?? '').trim();
                if (target && target !== '*' && !defs[target]) return `COUNT: no block "${target}".`;
                return pickArea(target || '*');
            },
        });
        return 'COUNT: specify the block to count';
    },
};

// ---------------------------------------------------------------------------
// Isolated block session helpers (Faz 5.15)
// ---------------------------------------------------------------------------

/** Ids of the entities currently tagged to a block scope. */
function scopeEntityIds(name: string): string[] {
    const { entities } = useAppStore.getState();
    return Object.values(entities)
        .filter((e) => (e.data as Record<string, unknown> | undefined)?.blockScope === name)
        .map((e) => e.id);
}

/** Convert the scope working set into the block definition (BSAVE core).
 *  Returns the component count, or -1 when the scope is empty. Existing
 *  dynamic-block metadata (visibility states, parameters, actions) is kept;
 *  self-referencing INSERTs (BTESTBLOCK's throwaway instances) stay out of
 *  the definition — a block cannot nest itself. */
function saveScopeToDef(name: string, basePoint: [number, number, number]): number {
    const app = useAppStore.getState();
    const scoped = Object.values(app.entities)
        .filter((e) => (e.data as Record<string, unknown> | undefined)?.blockScope === name)
        .filter((e) => !(
            (e.type === 'insert' || e.type === 'block')
            && String((e.data as Record<string, unknown>).blockName ?? '') === name
        ));
    if (scoped.length === 0) return -1;
    // Definition entities live relative to the base point (the same model
    // defineBlockWith uses with its centroid base).
    const local = scoped.map((e) => ({
        ...e,
        data: translateData(structuredClone(e.data), [-basePoint[0], -basePoint[1], -basePoint[2]]),
    }));
    const attributes: BlockAttribute[] = scoped
        .filter((e) => e.type === 'attdef')
        .map((e) => ({
            tag: String(e.data.tag ?? ''),
            prompt: String(e.data.prompt ?? ''),
            defaultValue: String(e.data.defaultValue ?? ''),
        }));
    const previous = useBlockStore.getState().defs[name];
    useBlockStore.getState().define({
        name,
        entities: local,
        basePoint,
        attributes,
        visibilityStates: previous?.visibilityStates,
        parameters: previous?.parameters,
        actions: previous?.actions,
    });
    return scoped.length;
}

/** Exit the editing session: optionally save, then remove the working set
 *  (one undoable step — U brings the working set back into the scope). */
function closeSession(session: { name: string; basePoint: [number, number, number] }, save: boolean): string {
    const ids = scopeEntityIds(session.name);
    if (save) saveScopeToDef(session.name, session.basePoint);
    undoableDelete(ids);
    useBlockStore.getState().setEditingBlock(null);
    useAppStore.getState().clearSelection();
    notify(i18n.t('tools.notify.bcloseClosed', { name: session.name, mode: save ? 'saved' : 'discarded' }), 'info');
    return save
        ? `BCLOSE: "${session.name}" saved; working set removed.`
        : `BCLOSE: "${session.name}" closed without saving.`;
}

// ---------------------------------------------------------------------------
// COUNT helpers (Faz 5.9)
// ---------------------------------------------------------------------------

/** Ray-cast point-in-polygon over the count area polygon. */
function pointInArea(x: number, y: number, area: [number, number][] | null): boolean {
    if (!area || area.length < 3) return true;
    let inside = false;
    for (let i = 0, j = area.length - 1; i < area.length; j = i++) {
        const [xi, yi] = area[i];
        const [xj, yj] = area[j];
        if (((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)) {
            inside = !inside;
        }
    }
    return inside;
}

/** Live tally shared by the COUNT runner and the Count palette: every insert
 *  reference matching the target (and inside the area), grouped by name. */
export function tallyBlocks(target: string, area: [number, number][] | null): { name: string; count: number; ids: string[] }[] {
    const { entities } = useAppStore.getState();
    const groups = new Map<string, string[]>();
    Object.values(entities).forEach((e) => {
        if (e.type !== 'insert') return;
        const name = String((e.data as Record<string, unknown>).blockName ?? '');
        if (!name) return;
        if (target !== '*' && name !== target) return;
        const p = (e.data as Record<string, unknown>).position as number[] | undefined;
        if (p && !pointInArea(p[0], p[1], area)) return;
        const list = groups.get(name) ?? [];
        list.push(e.id);
        groups.set(name, list);
    });
    return [...groups.entries()]
        .map(([name, ids]) => ({ name, count: ids.length, ids }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

/** Start a COUNT session: draw the locked, non-plotting boundary on the
 *  "0-CountArea" layer (when an area was given) and open the palette. */
function startCount(target: string, area: [number, number][] | null): string {
    const app = useAppStore.getState();
    // The boundary layer (OCS "0-CountArea"): locked, color 152, never plotted.
    const boundaryLayer = Object.values(app.layers).find((l) => l.name === '0-CountArea');
    let layerId = boundaryLayer?.id;
    if (!layerId) {
        layerId = `layer-count-area-${Date.now().toString(36)}`;
        app.addLayer({
            id: layerId,
            name: '0-CountArea',
            color: aciToHex(152),
            visible: true,
            locked: true,
            order: Object.keys(app.layers).length,
            plottable: false,
        });
    }
    let boundaryId: string | null = null;
    if (area && area.length >= 3) {
        const [id] = undoableAdd([makeEntity('polyline', 'CountArea', {
            vertices: area.map(([x, y]) => [x, y, 0]),
            closed: true,
            color: aciToHex(152),
        }, layerId)]);
        boundaryId = id;
    }
    useCountStore.getState().start({
        target: target === '*' ? '' : target,
        area,
        boundaryId,
    });
    // Open the Count palette so the live table is visible immediately.
    if (!useAppStore.getState().countOpen) useAppStore.getState().toggleCountPalette();
    const tally = tallyBlocks(target, area);
    const total = tally.reduce((acc, g) => acc + g.count, 0);
    return `COUNT: ${total} block reference(s)${target !== '*' ? ` of "${target}"` : ''}${area ? ' inside the area' : ''} — see the Count palette.`;
}

function defineBlock(name: string): string {
    const list = requireSelection('BLOCK');
    if (!list) return 'BLOCK: select objects first.';
    return defineBlockWith(name, list.map((e) => e.id));
}

function defineBlockWith(name: string, ids: string[]): string {
    const app = useAppStore.getState();
    const entities = ids.map((id) => app.entities[id]).filter(Boolean) as Entity[];
    if (entities.length === 0) return 'BLOCK: no valid objects selected.';
    const c = centroidOf(entities);
    const local = entities.map((e) => ({
        ...e,
        data: translateData(structuredClone(e.data), [-c[0], -c[1], -c[2]]),
    }));
    // The block's own attdefs become its attribute list.
    const attributes: BlockAttribute[] = entities
        .filter((e) => e.type === 'attdef')
        .map((e) => ({ tag: String(e.data.tag ?? ''), prompt: String(e.data.prompt ?? ''), defaultValue: String(e.data.defaultValue ?? '') }));
    useBlockStore.getState().define({ name, entities: local, basePoint: [0, 0, 0], attributes });
    undoableDelete(entities.map((e) => e.id));
    notify(i18n.t('tools.notify.blockDefined', { name: name, count: local.length }), 'success');
    return `BLOCK: "${name}" defined (${local.length} component(s), base at ${c.map((v) => v.toFixed(2)).join(',')}).`;
}
